import { Field } from "@deligoo/ui"
import { unwrapResult } from "@reduxjs/toolkit"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { useAppDispatch, useAppSelector } from "@/App"
import { ConfirmModal } from "@/components/ConfirmModal"
import { assignOrder, deletePayment, selectPaymentById, setPaymentAsTip } from "@/store/payments"
import { Payment } from "@/types"

const TOAST_OPTIONS = {
  toastId: "updatePayment",
  updateId: "updatePayment",
}

type ActionModalProps = {
  paymentId: Payment["id"]
  handleClose: () => void
}

export const SetAsTipModal = ({ paymentId, handleClose }: ActionModalProps) => {
  const dispatch = useAppDispatch()

  const { t } = useTranslation("payments")

  async function handleSetPaymentAsTip() {
    const response = await dispatch(setPaymentAsTip(paymentId)).then(unwrapResult)

    if (response.ok) {
      toast.success(t("payment_set_as_tip_success"), TOAST_OPTIONS)
    }

    handleClose()
  }

  return (
    <ConfirmModal
      title="Zaliczenie płatności jako bonus"
      description="Czy chcesz zaliczyć płatność jako bonus?"
      handleClose={handleClose}
      handleConfirm={handleSetPaymentAsTip}
      contentCenter
      autoFocusConfirm
    />
  )
}

export const EditPaymentModal = ({ paymentId, handleClose }: ActionModalProps) => {
  const payment = useAppSelector(selectPaymentById(paymentId))

  const [orderUid, setOrderUid] = useState(payment?.order_uid || "")
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  const { t } = useTranslation("payments")

  if (!payment) return null

  const transactionCode = payment.transaction_code

  async function handleEditPayment() {
    if (!orderUid || !transactionCode) {
      return toast.error(t("payment_update_empty_params"), TOAST_OPTIONS)
    }

    setIsLoading(true)

    const response = await dispatch(assignOrder({ transactionCode, body: { order_uid: orderUid } })).then(unwrapResult)

    if (response.ok) {
      toast.success(t("payment_update_success"), TOAST_OPTIONS)
    }

    setIsLoading(false)
    handleClose()
  }

  return (
    <ConfirmModal
      title="Edycja numeru zamówienia"
      handleConfirm={handleEditPayment}
      handleClose={handleClose}
      isLoading={isLoading}
      contentCenter
    >
      <Field
        label="Numer zamówienia"
        name="order_uid"
        inputProps={{
          onChange: (e) => setOrderUid(e.currentTarget.value),
          defaultValue: orderUid,
          placeholder: "12345",
          autoFocus: true,
        }}
      />
    </ConfirmModal>
  )
}

export const DeletePaymentModal = ({ paymentId, handleClose }: ActionModalProps) => {
  const dispatch = useAppDispatch()

  const { t } = useTranslation("payments")

  async function handleDeletePayment() {
    const response = await dispatch(deletePayment(paymentId)).then(unwrapResult)

    if (response.ok) {
      toast.success(t("payment_remove_success"), TOAST_OPTIONS)
    }

    handleClose()
  }

  return (
    <ConfirmModal
      title="Usunięcie transakcji"
      description="Czy chcesz usunąć transakcję?"
      handleConfirm={handleDeletePayment}
      handleClose={handleClose}
      contentCenter
    />
  )
}
